import {
    Alert,
    Box,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TablePagination,
    TableRow,
    Typography,
} from "@mui/material";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAllLicensesByResellerId } from "../../../actions/licensesActions";

import {
    aggregateSummariesByModuleAndUnit,
    DEFAULT_PAGE_SIZE,
    EM_DASH,
    fetchQuotaSummary,
    fetchQuotaUsageHistory,
    formatAmount,
    formatOccurredAt,
    formatQuotaTypeLabel,
    formatUnitLabel,
    getHistoryPanelLabel,
    getModuleCellText,
    quotaIdsEqual,
} from "./quotaConsumptionUtils";

/**
 * Summary table: one row per module + unit (balances pre-aggregated in utils).
 */
const QuotaSummaryTable = memo(function QuotaSummaryTable({ rows, selectedQuotaIds, onRowClick }) {
    return (
        <TableContainer>
            <Table size="medium" id="quota-consumption-summary-table">
                <TableHead>
                    <TableRow>
                        <TableCell>Modul</TableCell>
                        <TableCell>Einheit</TableCell>
                        <TableCell align="right">Guthaben</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows.map((row) => (
                        <TableRow
                            key={row.groupKey}
                            hover
                            selected={quotaIdsEqual(selectedQuotaIds, row.quotaIds)}
                            onClick={() => onRowClick(row)}
                            sx={{ cursor: "pointer" }}
                        >
                            <TableCell>{getModuleCellText(row)}</TableCell>
                            <TableCell>{formatUnitLabel(row.unit)}</TableCell>
                            <TableCell align="right">{formatAmount(row.balanceTotal)}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
});
QuotaSummaryTable.displayName = "QuotaSummaryTable";

/**
 * Paginated usage history for selected quota definition ids.
 */
const QuotaUsageHistoryTable = memo(function QuotaUsageHistoryTable({
    usageRows,
    pageIndexZeroBased,
    rowsPerPage,
    totalCount,
    onPageChange,
    onRowsPerPageChange,
}) {
    return (
        <TableContainer>
            <Table size="small" id="quota-consumption-detail-table">
                <TableHead>
                    <TableRow>
                        <TableCell>Datum</TableCell>
                        <TableCell>Modul</TableCell>
                        <TableCell>Einheit</TableCell>
                        <TableCell align="right">Verbrauch</TableCell>
                        <TableCell align="right">Gutschrift</TableCell>
                        <TableCell>Typ</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {usageRows.map((row) => {
                        const usageTypeLower = (row.usageType || "").toLowerCase();
                        const isGrant = usageTypeLower.startsWith("grant_");
                        return (
                            <TableRow key={row.quotaUsageId}>
                                <TableCell>{formatOccurredAt(row.occurredAt)}</TableCell>
                                <TableCell>{row.moduleName || `Quota ${row.quotaId}`}</TableCell>
                                <TableCell>{formatUnitLabel(row.unit)}</TableCell>
                                <TableCell align="right">{!isGrant ? formatAmount(0 - row.delta) : EM_DASH}</TableCell>
                                <TableCell align="right">{isGrant ? formatAmount(row.delta) : EM_DASH}</TableCell>
                                <TableCell>{formatQuotaTypeLabel(row.usageType)}</TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
                <TableFooter>
                    <TableRow>
                        <TablePagination
                            id="quota-consumption-detail-pagination"
                            rowsPerPageOptions={[10, 25, 50]}
                            colSpan={6}
                            count={totalCount}
                            rowsPerPage={rowsPerPage}
                            page={pageIndexZeroBased}
                            onPageChange={onPageChange}
                            onRowsPerPageChange={onRowsPerPageChange}
                            labelRowsPerPage="Zeilen"
                            labelDisplayedRows={({ from, to, count }) =>
                                `${from}–${to} von ${count !== -1 ? count : `mehr als ${to}`}`
                            }
                        />
                    </TableRow>
                </TableFooter>
            </Table>
        </TableContainer>
    );
});
QuotaUsageHistoryTable.displayName = "QuotaUsageHistoryTable";

const QuotaConsumptionPage = () => {
    const dispatch = useDispatch();
    const identity = useSelector((state) => state.identity);

    const [licenses, setLicenses] = useState([]);
    const [licensesLoading, setLicensesLoading] = useState(true);
    const [licensesError, setLicensesError] = useState(null);

    const [selectedLicenseId, setSelectedLicenseId] = useState("");

    const [summaryData, setSummaryData] = useState(null);
    const [summaryLoading, setSummaryLoading] = useState(false);
    const [error, setError] = useState(null);

    const [detailQuotaIds, setDetailQuotaIds] = useState(null);
    const [detailQuotaLabel, setDetailQuotaLabel] = useState("");
    const [detailData, setDetailData] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [detailPage, setDetailPage] = useState(0);
    const [detailRowsPerPage, setDetailRowsPerPage] = useState(DEFAULT_PAGE_SIZE);

    const handleLoadSummary = useCallback(async (licenseId) => {
        if (!licenseId) {
            setError("Bitte eine Lizenz auswählen.");
            return;
        }
        setError(null);
        setSummaryLoading(true);
        setSummaryData(null);
        setDetailData(null);
        setDetailQuotaIds(null);
        setDetailQuotaLabel("");
        setDetailPage(0);
        try {
            const result = await fetchQuotaSummary(Number(licenseId));
            setSummaryData(result);
        } catch (e) {
            setError(e?.message || String(e));
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!identity?.user?.resellerId) {
            setLicensesLoading(false);
            return;
        }
        setLicensesLoading(true);
        dispatch(getAllLicensesByResellerId(identity.user.resellerId))
            .then((json) => {
                setLicenses(Array.isArray(json) ? json : []);
                if (json?.length) {
                    setSelectedLicenseId(json[0].LicenseId);
                    handleLoadSummary(json[0].LicenseId);
                }
                setLicensesError(null);
            })
            .catch((err) => {
                setLicensesError(err?.message || String(err));
            })
            .finally(() => setLicensesLoading(false));
    }, [dispatch, identity?.user?.resellerId, handleLoadSummary]);

    const loadHistory = useCallback(
        async (quotaIds, label, pageIndexZeroBased, rowsPerPage) => {
            if (!selectedLicenseId || !quotaIds?.length) {
                return;
            }
            setDetailLoading(true);
            setError(null);
            try {
                const result = await fetchQuotaUsageHistory({
                    licenseId: Number(selectedLicenseId),
                    quotaIds,
                    page: pageIndexZeroBased + 1,
                    pageSize: rowsPerPage,
                });
                setDetailData(result);
                setDetailQuotaIds([...quotaIds]);
                if (label) {
                    setDetailQuotaLabel(label);
                }
                if (result.pagination) {
                    setDetailPage(result.pagination.page - 1);
                    setDetailRowsPerPage(result.pagination.pageSize);
                }
            } catch (e) {
                setError(e?.message || String(e));
                setDetailData(null);
            } finally {
                setDetailLoading(false);
            }
        },
        [selectedLicenseId]
    );

    const handleSummaryRowClick = useCallback(
        (row) => {
            setDetailPage(0);
            loadHistory(row.quotaIds, getHistoryPanelLabel(row), 0, detailRowsPerPage);
        },
        [loadHistory, detailRowsPerPage]
    );

    const handleDetailPageChange = useCallback(
        (_event, newPage) => {
            if (!detailQuotaIds?.length) {
                return;
            }
            setDetailPage(newPage);
            loadHistory(detailQuotaIds, detailQuotaLabel, newPage, detailRowsPerPage);
        },
        [detailQuotaIds, detailQuotaLabel, detailRowsPerPage, loadHistory]
    );

    const handleDetailRowsPerPageChange = useCallback(
        (event) => {
            const next = parseInt(event.target.value, 10);
            setDetailRowsPerPage(next);
            setDetailPage(0);
            if (detailQuotaIds?.length) {
                loadHistory(detailQuotaIds, detailQuotaLabel, 0, next);
            }
        },
        [detailQuotaIds, detailQuotaLabel, loadHistory]
    );

    const handleBackToSummary = useCallback(() => {
        setDetailData(null);
        setDetailQuotaIds(null);
        setDetailQuotaLabel("");
        setDetailPage(0);
    }, []);

    const summaryRowsGrouped = useMemo(
        () => aggregateSummariesByModuleAndUnit(summaryData?.quotaSummaries),
        [summaryData?.quotaSummaries]
    );

    if (licensesLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }} id="quota-consumption-loading">
                <CircularProgress aria-label="Laden" />
            </Box>
        );
    }

    if (licensesError) {
        return (
            <Alert severity="error" sx={{ mt: 2 }} id="quota-consumption-licenses-error">
                {licensesError}
            </Alert>
        );
    }

    const showSummaryTable = Boolean(summaryData && !summaryLoading);
    const showDetail = Boolean(detailData?.pagination);
    const detailTotal = showDetail && detailData.pagination ? detailData.pagination.total : 0;

    return (
        <Paper
            className="quota-consumption-page"
            sx={{ margin: "auto", marginTop: 4, maxWidth: 1200 }}
            id="quota-consumption-page"
        >
            <Box sx={{ p: 2 }}>
                <Typography variant="h4" component="h1" id="quota-consumption-title">
                    Kontingente und Verbrauch
                </Typography>

                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 4,
                        alignItems: "center",
                        mb: 2,
                        mt: 2,
                    }}
                >
                    <FormControl sx={{ minWidth: 280 }} id="quota-consumption-license-field">
                        <InputLabel id="quota-consumption-license-label">Lizenz</InputLabel>
                        <Select
                            labelId="quota-consumption-license-label"
                            label="Lizenz"
                            value={selectedLicenseId}
                            onChange={(e) => {
                                setSelectedLicenseId(e.target.value);
                                handleLoadSummary(Number(e.target.value));
                            }}
                            data-testid="quota-consumption-license-select"
                        >
                            {licenses.map((lic) => (
                                <MenuItem key={lic.LicenseId} value={lic.LicenseId}>
                                    {lic.StoreName || lic.LicenseNumber} ({lic.LicenseNumber})
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                {error && (
                    <Alert severity="error" sx={{ mb: 2 }} id="quota-consumption-error">
                        {error}
                    </Alert>
                )}

                {summaryLoading && (
                    <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                        <CircularProgress size={32} aria-label="Daten werden geladen" />
                    </Box>
                )}

                {showSummaryTable && (
                    <Box id="quota-consumption-result">
                        {!summaryData.tenantConnected || !summaryRowsGrouped.length ? (
                            <Alert severity="info" id="quota-consumption-empty">
                                Keine Kontingent-Daten für diese Auswahl.
                            </Alert>
                        ) : (
                            <>
                                <Typography variant="h6" sx={{ mb: 2 }}>
                                    Aktueller Stand (kumuliert, Zeile anklicken für Verlauf)
                                </Typography>
                                <QuotaSummaryTable
                                    rows={summaryRowsGrouped}
                                    selectedQuotaIds={detailQuotaIds}
                                    onRowClick={handleSummaryRowClick}
                                />
                            </>
                        )}
                    </Box>
                )}

                {(detailLoading || showDetail) && (
                    <Box sx={{ mt: 4 }} id="quota-consumption-detail">
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                mb: 2,
                                flexWrap: "wrap",
                                justifyContent: "space-between",
                            }}
                        >
                            <Typography variant="h6">
                                Verlauf ·{" "}
                                {detailQuotaLabel ||
                                    (detailQuotaIds?.length ? `Kontingente ${detailQuotaIds.join(", ")}` : "")}
                            </Typography>
                            <Button
                                id="quota-consumption-back-button"
                                variant="outlined"
                                size="small"
                                onClick={handleBackToSummary}
                                disabled={detailLoading}
                            >
                                Schließen
                            </Button>
                        </Box>
                        {detailLoading && (
                            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                                <CircularProgress size={28} aria-label="Verlauf wird geladen" />
                            </Box>
                        )}
                        {showDetail && !detailLoading && (
                            <QuotaUsageHistoryTable
                                usageRows={detailData.usageRows || []}
                                pageIndexZeroBased={detailPage}
                                rowsPerPage={detailRowsPerPage}
                                totalCount={detailTotal}
                                onPageChange={handleDetailPageChange}
                                onRowsPerPageChange={handleDetailRowsPerPageChange}
                            />
                        )}
                    </Box>
                )}
            </Box>
        </Paper>
    );
};

export default QuotaConsumptionPage;
